import { render, h } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { usePpPortalAuthenticated } from '../lib/pp-user-auth.js';
import { HeroQueryCard } from '../components/landing/HeroQueryCard.jsx';
import { LandingFooterBand } from '../components/landing/LandingFooterBand.jsx';
import { readHavaleBankBadges } from '../lib/havale-bank-badges.js';
import '../styles/landing.css';

const PORTAL_SANDIK_URL = '/portal/recent-queries/?list_mode=all';

function parseFreeReportSlideUrls(raw) {
  if (!raw || typeof raw !== 'string') {
    return [];
  }
  return raw
    .split('|')
    .map((s) => s.trim())
    .filter(Boolean);
}

function readAssets() {
  const el = document.getElementById('landing-root');
  if (!el) {
    return {
      analysis: '',
      terrain: '',
      moduleSplitVector: '',
      moduleSplitIso: '',
      moduleSplitSat: '',
      moduleAraziThreeD: '',
      moduleEdgeParcel: '',
      moduleModelDesign: '',
      prosorguOneClick: '',
      prosorguPuanSistemi: '',
      prosorguAraziSkoru: '',
      prosorguSinyaller: '',
      prosorguIlanDetay: '',
      prosorguBolunebilirlik: '',
      prosorguMeyveBahcesi: '',
      heroBg: '/media/landingpage/sluet3.png',
      favicon: '/static/img/favicon.ico',
      freeReportSlides: [],
      bankBadges: [],
      siteSslType: 'dv',
      companyAddress: '',
    };
  }
  return {
    analysis: el.dataset.analysisImg || '',
    terrain: el.dataset.terrainImg || '',
    moduleSplitVector: el.dataset.moduleSplitVector || '',
    moduleSplitIso: el.dataset.moduleSplitIso || '',
    moduleSplitSat: el.dataset.moduleSplitSat || '',
    moduleAraziThreeD: el.dataset.moduleAraziThreeD || '',
    moduleEdgeParcel: el.dataset.moduleEdgeParcel || '',
    moduleModelDesign: el.dataset.moduleModelDesign || '',
    prosorguOneClick: el.dataset.prosorguOneClick || '',
    prosorguPuanSistemi: el.dataset.prosorguPuanSistemi || '',
    prosorguAraziSkoru: el.dataset.prosorguAraziSkoru || '',
    prosorguSinyaller: el.dataset.prosorguSinyaller || '',
    prosorguIlanDetay: el.dataset.prosorguIlanDetay || '',
    prosorguBolunebilirlik: el.dataset.prosorguBolunebilirlik || '',
    prosorguMeyveBahcesi: el.dataset.prosorguMeyveBahcesi || '',
    heroBg: (el.dataset.heroBg || '/media/landingpage/sluet3.png').trim(),
    favicon: (el.dataset.favicon || '/static/img/favicon.ico').trim(),
    freeReportSlides: parseFreeReportSlideUrls(el.dataset.freeReportSlides || ''),
    bankBadges: readHavaleBankBadges(),
    siteSslType: (el.dataset.siteSslType || 'dv').trim(),
    companyAddress: (el.dataset.companyAddress || '').trim(),
  };
}

const FREE_REPORT_SLIDES_META = [
  {
    alt: 'ProParcel analiz raporu: özet sayfa, harita, fiyat ve risk tabloları',
    caption: 'Özet analiz — skor, fiyat ve risk',
  },
  {
    alt: 'ProParcel analiz raporu: yapı detayları, tapu ve altyapı bilgileri',
    caption: 'Yapı detayları',
  },
  {
    alt: 'ProParcel analiz raporu: detaylı fiyat analizi (DFA) ve değerleme özeti',
    caption: 'Detaylı fiyat analizi (DFA)',
  },
  {
    alt: 'ProParcel analiz raporu: skor özeti, puan sistemi ve meyve bahçesi skoru',
    caption: 'Skor özeti',
  },
  {
    alt: 'ProParcel analiz raporu: mahalle satışları ve yıl bazlı liste',
    caption: 'Mahalle satışları',
  },
  {
    alt: 'ProParcel analiz raporu: komşu mahalle analizi ve fiyat haritası',
    caption: 'Komşu mahalle analizi',
  },
  {
    alt: 'ProParcel analiz raporu: KM analizi ve karşılaştırmalı piyasa özeti',
    caption: 'KM analizi',
  },
  {
    alt: 'ProParcel analiz raporu: parsel eğim ve morfoloji',
    caption: 'Parsel eğim',
  },
  {
    alt: 'ProParcel analiz raporu: kenar ölçüm ve parsel geometrisi',
    caption: 'Kenar ölçüm',
  },
  {
    alt: 'ProParcel analiz raporu: bölünebilirlik ve imarsız tarım kriterleri',
    caption: 'Bölünebilirlik',
  },
];

function FreeReportSection({ slides, mapUrl }) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(null);
  const n = slides.length;

  if (!n) {
    return null;
  }

  const go = (delta) => setIndex((i) => (i + delta + n) % n);
  const current = slides[index];

  return (
    <section
      class="pp-landing-section pp-landing-section--alt pp-landing-free-report"
      aria-labelledby="free-report-heading"
    >
      <div class="pp-landing-inner">
        <div class="pp-landing-section__head pp-landing-free-report__head">
          <h2 id="free-report-heading">Ücretsiz Rapor Oluştur</h2>
          <p>
            Parseliniz için üretilen <strong>analiz raporundan</strong> örnek sayfalar: özet, fiyat,
            skor, mahalle ve teknik modüller. Ok tuşları veya oklarla slaytlar arasında gezinebilirsiniz.
          </p>
        </div>

        <div
          class="pp-landing-free-report__carousel"
          role="region"
          aria-roledescription="Slayt gösterisi"
          aria-label="Rapor örnekleri"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') {
              e.preventDefault();
              go(-1);
            }
            if (e.key === 'ArrowRight') {
              e.preventDefault();
              go(1);
            }
          }}
        >
          <p class="pp-landing-free-report__sr" aria-live="polite">
            Slayt {index + 1} / {n}: {current.caption}
          </p>

          <div
            class="pp-landing-free-report__viewport"
            onTouchStart={(e) => {
              if (e.touches[0]) {
                touchStartX.current = e.touches[0].clientX;
              }
            }}
            onTouchEnd={(e) => {
              const start = touchStartX.current;
              touchStartX.current = null;
              if (start == null || !e.changedTouches[0]) {
                return;
              }
              const dx = e.changedTouches[0].clientX - start;
              if (dx > 52) {
                go(-1);
              } else if (dx < -52) {
                go(1);
              }
            }}
          >
            <figure class="pp-landing-free-report__figure">
              <img
                key={current.src}
                src={current.src}
                alt={current.alt}
                loading={index === 0 ? 'eager' : 'lazy'}
                decoding="async"
              />
              <figcaption class="pp-landing-free-report__figcaption">{current.caption}</figcaption>
            </figure>
          </div>

          <div class="pp-landing-free-report__controls">
            <button
              type="button"
              class="pp-landing-free-report__nav pp-landing-free-report__nav--prev"
              onClick={() => go(-1)}
              aria-label="Önceki slayt"
            >
              <span aria-hidden="true">‹</span>
            </button>
            <div class="pp-landing-free-report__dots" role="tablist" aria-label="Slayt seçimi">
              {slides.map((slide, i) => (
                <button
                  key={slide.src}
                  type="button"
                  role="tab"
                  aria-selected={i === index}
                  aria-label={`${slide.caption} — slayt ${i + 1}`}
                  class={i === index ? 'is-active' : ''}
                  onClick={() => setIndex(i)}
                />
              ))}
            </div>
            <button
              type="button"
              class="pp-landing-free-report__nav pp-landing-free-report__nav--next"
              onClick={() => go(1)}
              aria-label="Sonraki slayt"
            >
              <span aria-hidden="true">›</span>
            </button>
          </div>
        </div>

        <div class="pp-landing-free-report__cta">
          <a class="pp-landing-hero__cta" href={mapUrl}>
            Haritada analiz yap — raporunu oluştur
          </a>
        </div>
      </div>
    </section>
  );
}

function IconSplit() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 6h8v4H4V6zm8 8H4v4h8v-4z" fill="currentColor" opacity="0.85" />
      <path d="M14 4h6v16h-6V4z" fill="currentColor" opacity="0.45" />
      <path d="M13 12h1v1h-1v-1z" fill="currentColor" />
    </svg>
  );
}

function IconChartMap() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 18V8l4-2 4 2 4-2 4 2v10l-4-2-4 2-4-2-4 2z"
        stroke="currentColor"
        strokeWidth="1.6"
        fill="none"
      />
      <path d="M9 14l2-2 2 3 3-4" stroke="currentColor" strokeWidth="1.6" fill="none" />
    </svg>
  );
}

function IconExpert() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M6 18c0-3.3 2.7-6 6-6s6 2.7 6 6"
        stroke="currentColor"
        strokeWidth="1.6"
        fill="none"
      />
    </svg>
  );
}

function IconHome() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 10.5L12 4l8 6.5V20h-5v-6H9v6H4v-9.5z" stroke="currentColor" strokeWidth="1.6" fill="none" />
    </svg>
  );
}

function IconMap3d() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 16l6-3 4 2 6-3v4l-6 3-4-2-6 3V16z"
        stroke="currentColor"
        strokeWidth="1.5"
        fill="none"
        strokeLinejoin="round"
      />
      <path d="M4 10l6-3 4 2 6-3v4l-6 3-4-2-6 3V10z" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.55" />
      <path d="M10 7v10M14 9v10" stroke="currentColor" strokeWidth="1.2" opacity="0.35" />
    </svg>
  );
}

function IconStepMap() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 18V8l4-2 4 2 4-2 4 2v10l-4-2-4 2-4-2-4 2z" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="12" cy="12" r="2" fill="currentColor" />
    </svg>
  );
}

function IconStepPlay() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.4" />
      <path d="M10 9l6 3-6 3V9z" fill="currentColor" />
    </svg>
  );
}

function IconStepReport() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M7 4h10v16H7V4z" stroke="currentColor" strokeWidth="1.4" />
      <path d="M9 8h6M9 12h6M9 16h4" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

function ExpertIllustration() {
  return (
    <svg class="pp-landing-expert-svg" viewBox="0 0 400 280" aria-hidden="true">
      <defs>
        <linearGradient id="pp-expert-floor" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f8fafc" />
          <stop offset="100%" stopColor="#e2e8f0" />
        </linearGradient>
        <filter id="pp-expert-shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="8" stdDeviation="12" floodOpacity="0.12" />
        </filter>
      </defs>
      <rect width="400" height="280" fill="url(#pp-expert-floor)" rx="16" />
      <ellipse cx="200" cy="248" rx="140" ry="18" fill="#cbd5e1" opacity="0.35" />
      <g filter="url(#pp-expert-shadow)">
        <rect x="120" y="48" width="160" height="110" rx="12" fill="#fff" stroke="#e2e8f0" strokeWidth="2" />
        <path d="M135 115h130M135 95h90" stroke="#cbd5e1" strokeWidth="4" strokeLinecap="round" />
        <rect x="145" y="68" width="50" height="36" rx="4" fill="#dbeafe" stroke="#93c5fd" />
        <circle cx="248" cy="86" r="14" fill="#22c55e" opacity="0.35" />
        <path d="M135 130h110" stroke="#e2e8f0" strokeWidth="3" />
      </g>
      <ellipse cx="200" cy="210" rx="36" ry="10" fill="#94a3b8" opacity="0.25" />
      <g>
        <circle cx="200" cy="118" r="28" fill="#fcd9bd" />
        <path d="M172 118c0-15 12-28 28-28s28 13 28 28" fill="#0f172a" opacity="0.85" />
        <rect x="175" y="145" width="50" height="62" rx="8" fill="#1e3a8a" />
        <rect x="188" y="168" width="64" height="48" rx="6" fill="#1d4ed8" />
        <rect x="198" y="176" width="44" height="32" rx="4" fill="#93c5fd" opacity="0.5" />
      </g>
    </svg>
  );
}

/** Hero içi kompakt tek parsel → 5 parça animasyonu */
/** Güçlü modüller — hisseli bölme bölümü için büyük animasyon şeridi */
function ModuleSplitAnimStrip() {
  return (
    <div class="pp-landing-modanim" aria-hidden="true">
      <div class="pp-landing-modanim__bar">
        <span>Tek parsel</span>
        <span>5 parça · eşit alan</span>
      </div>
      <div class="pp-landing-modanim__stage">
        <div class="pp-landing-modanim__single">
          <span class="pp-landing-modanim__dim">Parsel</span>
        </div>
        <div class="pp-landing-modanim__multi">
          <span class="pp-landing-modanim__cell pp-landing-modanim__cell--1" />
          <span class="pp-landing-modanim__cell pp-landing-modanim__cell--2" />
          <span class="pp-landing-modanim__cell pp-landing-modanim__cell--3" />
          <span class="pp-landing-modanim__cell pp-landing-modanim__cell--4" />
          <span class="pp-landing-modanim__cell pp-landing-modanim__cell--5" />
        </div>
        <div class="pp-landing-modanim__sweep" />
      </div>
      <p class="pp-landing-modanim__hint">Yatay bölme — örnek akış</p>
    </div>
  );
}

function ModuleSplitSpotlight({ assets, mapUrl }) {
  const wrapRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1, rootMargin: '0px 0px -8% 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={wrapRef}
      class={
        visible
          ? 'pp-landing-module-split pp-landing-module-split--visible'
          : 'pp-landing-module-split'
      }
    >
      <div class="pp-landing-module-split__intro">
        <span class="pp-landing-module-split__step" aria-hidden="true">
          1
        </span>
        <div>
          <h3 class="pp-landing-module-split__title">Hisseli parsel bölme</h3>
          <p class="pp-landing-module-split__lead">
            Paydaş sayısına göre parseli bölün; sınırlar, renkler ve alan etiketleri otomatik üretilir.
            Profil (tarla / arsa), bölme modu, parça sayısı ve yönü seçip <strong>Böl</strong> ile sonucu
            görün.
          </p>
        </div>
      </div>

      <div class="pp-landing-module-split__grid">
        <div class="pp-landing-module-split__copy">
          <ul class="pp-landing-module-split__bullets">
            <li>
              <strong>Profil</strong> — tarla veya arsa
            </li>
            <li>
              <strong>Bölme modu</strong> — adet, alan veya özel m²
            </li>
            <li>
              <strong>Yön</strong> — yatay, dikey veya otomatik
            </li>
            <li>
              <strong>Görünüm</strong> — uydu / vektör / 3D üzerinde renkli parçalar ve m²
            </li>
          </ul>
          <a class="pp-landing-module-split__cta" href={mapUrl}>
            Haritada dene
          </a>
        </div>
        <div class="pp-landing-module-split__anim">
          <ModuleSplitAnimStrip />
        </div>
      </div>

      <div class="pp-landing-module-split__gallery" aria-label="Hisseli bölme ekran görüntüleri">
        <figure class="pp-landing-module-split__shot pp-landing-module-split__shot--a">
          {assets.moduleSplitVector ? (
            <img
              src={assets.moduleSplitVector}
              alt="ProParcel hisseli parsel bölme: vektör haritada beş renkli yatay parça ve alan etiketleri"
              loading="lazy"
            />
          ) : null}
          <figcaption>Vektör görünüm — geometri ve bölümler net okunur</figcaption>
        </figure>
        <figure class="pp-landing-module-split__shot pp-landing-module-split__shot--b">
          {assets.moduleSplitIso ? (
            <img
              src={assets.moduleSplitIso}
              alt="Üç boyutlu isometrik arazi üzerinde çoklu parsel bölümlemesi"
              loading="lazy"
            />
          ) : null}
          <figcaption>3D sahne — arazi ve çevre bağlamında parçalar</figcaption>
        </figure>
        <figure class="pp-landing-module-split__shot pp-landing-module-split__shot--c">
          {assets.moduleSplitSat ? (
            <img
              src={assets.moduleSplitSat}
              alt="Uydu haritada yatay bölünmüş parsel, beş renkli şerit ve m² etiketleri"
              loading="lazy"
            />
          ) : null}
          <figcaption>Uydu — gerçek arazi dokusu üzerinde bölme</figcaption>
        </figure>
      </div>

    </div>
  );
}

function ModuleEdgeParcelSpotlight({ assets, mapUrl }) {
  const wrapRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1, rootMargin: '0px 0px -8% 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={wrapRef}
      class={
        visible
          ? 'pp-landing-module-edge pp-landing-module-edge--visible'
          : 'pp-landing-module-edge'
      }
    >
      <div class="pp-landing-module-edge__intro">
        <span class="pp-landing-module-edge__step" aria-hidden="true">
          2
        </span>
        <div>
          <h3 class="pp-landing-module-edge__title">Kenar ölçüleri ve parsel bilgisi</h3>
          <p class="pp-landing-module-edge__lead">
            Seçtiğiniz parselin her kenarında metre cinsinden ölçü; köşe parselde yola cephe uzunlukları
            ve sokak adlarıyla birlikte okunur. Ada, parsel ve toplam alan (m²) tek panelde.
          </p>
        </div>
      </div>

      <div class="pp-landing-module-edge__grid">
        <div class="pp-landing-module-edge__copy">
          <ul class="pp-landing-module-edge__bullets">
            <li>
              <strong>Kenar ölçüleri</strong> — dört cephe için ayrı metre etiketleri
            </li>
            <li>
              <strong>Yola cephe</strong> — hangi sokakta kaç metre ön yüz
            </li>
            <li>
              <strong>Parsel bilgisi</strong> — ada / parsel numarası ve toplam alan
            </li>
            <li>
              <strong>Harita üstü</strong> — uydu / vektör görünümde net sınır çizimi
            </li>
          </ul>
          <a class="pp-landing-module-edge__cta" href={mapUrl}>
            Parseli haritada aç
          </a>
        </div>
        <div class="pp-landing-module-edge__visual">
          <div class="pp-landing-module-edge__frame">
            {assets.moduleEdgeParcel ? (
              <img
                class="pp-landing-module-edge__img"
                src={assets.moduleEdgeParcel}
                alt="Uydu haritada vurgulu parsel: ada ve parsel numarası, toplam alan, kenar kenar metre ölçüleri ve sokak isimleri"
                loading="lazy"
              />
            ) : null}
          </div>
          <p class="pp-landing-module-edge__caption">Örnek görünüm — ölçü ve parsel bilgisi aynı ekranda</p>
        </div>
      </div>
    </div>
  );
}

function Module3dSpotlight({ assets, mapUrl }) {
  const wrapRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1, rootMargin: '0px 0px -8% 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={wrapRef}
      class={
        visible
          ? 'pp-landing-module-3d pp-landing-module-3d--visible'
          : 'pp-landing-module-3d'
      }
    >
      <div class="pp-landing-module-3d__intro">
        <span class="pp-landing-module-3d__step" aria-hidden="true">
          3
        </span>
        <div>
          <h3 class="pp-landing-module-3d__title">Arazi 3D görünümü</h3>
          <p class="pp-landing-module-3d__lead">
            Parseli düz haritadan çıkarıp gerçek arazi dokusu ve yükselti ile görün. Vadiler, yollar ve
            yerleşim katmanları tek bakışta; seçtiğiniz alan vurgulu okunur.
          </p>
        </div>
      </div>

      <div class="pp-landing-module-3d__grid">
        <div class="pp-landing-module-3d__copy">
          <ul class="pp-landing-module-3d__bullets">
            <li>
              <strong>Yüksek açı</strong> — drone hissiyle geniş vadide parsel bağlamı
            </li>
            <li>
              <strong>3D yükselti</strong> — eğim, sis ve ışıkla derinlik
            </li>
            <li>
              <strong>Parsel vurgusu</strong> — ilgilendiğiniz alan net sınırla öne çıkar
            </li>
            <li>
              <strong>Esnek geçiş</strong> — istediğiniz an klasik 2D haritaya dönün
            </li>
          </ul>
          <a class="pp-landing-module-3d__cta" href={mapUrl}>
            3D görünümü aç
          </a>
        </div>
        <div class="pp-landing-module-3d__visual">
          <div class="pp-landing-module-3d__frame">
            {assets.moduleAraziThreeD ? (
              <img
                class="pp-landing-module-3d__img"
                src={assets.moduleAraziThreeD}
                alt="Vadi üzerinde vurgulu arazi parseli: 3D yükselti, dağlar ve yol ağı"
                loading="lazy"
              />
            ) : null}
          </div>
          <p class="pp-landing-module-3d__caption">Örnek görünüm — parsel ve çevre aynı sahnede</p>
        </div>
      </div>
    </div>
  );
}

function Module3dDesignSpotlight({ assets, mapUrl }) {
  const wrapRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1, rootMargin: '0px 0px -8% 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={wrapRef}
      class={
        visible
          ? 'pp-landing-module-design pp-landing-module-design--visible'
          : 'pp-landing-module-design'
      }
    >
      <div class="pp-landing-module-design__intro">
        <span class="pp-landing-module-design__step" aria-hidden="true">
          4
        </span>
        <div>
          <h3 class="pp-landing-module-design__title">Arazine 3D modeller, tasarım</h3>
          <p class="pp-landing-module-design__lead">
            Parselinizin 3D yüzeyi üzerinde kütüphaneden yapı, araç, ağaç ve peyzaj objelerini yerleştirin.
            Mesafe ve alan ölçün, geometrik şekiller ve notlar ekleyerek projeyi sahada deneyin.
          </p>
        </div>
      </div>

      <div class="pp-landing-module-design__grid">
        <div class="pp-landing-module-design__copy">
          <ul class="pp-landing-module-design__bullets">
            <li>
              <strong>Model kütüphanesi</strong> — villa, köy evi, araç, ağaç vb.; satın aldığınız modeller
              sınırsız kullanım
            </li>
            <li>
              <strong>Yerleştirme</strong> — seçtiğiniz modeli araziye ekleyin, sahneyi dilediğiniz gibi
              doldurun
            </li>
            <li>
              <strong>Sol araç çubuğu</strong> — parsel seç, <em>mesafe</em> ve <em>alan</em> ölçümü, kare /
              üçgen / çokgen / çizgi, ok, nokta, metin; <em>temizle</em>
            </li>
            <li>
              <strong>Paneller</strong> — Model, Bina, Şekil ve Metin sekmeleriyle nesne ve düzen kontrolü
            </li>
          </ul>
          <a class="pp-landing-module-design__cta" href={mapUrl}>
            3D tasarıma geç
          </a>
        </div>
        <div class="pp-landing-module-design__visual">
          <div class="pp-landing-module-design__frame">
            {assets.moduleModelDesign ? (
              <img
                class="pp-landing-module-design__img"
                src={assets.moduleModelDesign}
                alt="3D arazi üzerinde villa, araç ve ağaçlar; sol tarafta ölçüm ve çizim araçları, sağda model kütüphanesi"
                loading="lazy"
              />
            ) : null}
          </div>
          <p class="pp-landing-module-design__caption">
            Örnek arayüz — modelleri arazinize yerleştirip ölçüm ve tasarım yapın
          </p>
        </div>
      </div>
    </div>
  );
}

function ProsorguIlanSection({ assets, mapUrl }) {
  const wrapRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.08, rootMargin: '0px 0px -6% 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={wrapRef}
      class={
        visible
          ? 'pp-landing-section pp-landing-section--alt pp-landing-section--prosorgu pp-landing-prosorgu pp-landing-prosorgu--visible'
          : 'pp-landing-section pp-landing-section--alt pp-landing-section--prosorgu pp-landing-prosorgu'
      }
      aria-labelledby="prosorgu-heading"
    >
      <div class="pp-landing-inner">
        <div class="pp-landing-section__head pp-landing-section__head--wide">
          <h2 id="prosorgu-heading">Tek tıkla ProSorgu veya İlan Ver</h2>
          <p>
            Parseli seçtikten sonra TKGM verisiyle arazi sınıfını görürsünüz; onayladığınız tipi
            seçip <strong>ProSorgu</strong> ile profesyonel analizi başlatın veya{' '}
            <strong>ProSorgu + İlan Ver</strong> ile sorguyu ve ilan taslağını tek akışta açın.
          </p>
        </div>

        <div class="pp-landing-prosorgu__hero">
          <div class="pp-landing-prosorgu__hero-copy">
            <h3 class="pp-landing-prosorgu__h3">Sorgu öncesi adım</h3>
            <ul class="pp-landing-prosorgu__list">
              <li>TKGM kayıtlı arazi tipi ve önerilen sınıf (ör. tarla) bilgisi</li>
              <li>Arsa, tarla, köy içi, ticari, konut arsası ve daha fazlası — tek ekranda seçim</li>
              <li>Hisseli parsel bilgileri için kısayol</li>
              <li>İki ana eylem: yalnız ProSorgu veya ProSorgu + İlan Ver</li>
            </ul>
            <a class="pp-landing-prosorgu__cta" href={mapUrl}>
              Akışı haritada aç
            </a>
          </div>
          <figure class="pp-landing-prosorgu__figure pp-landing-prosorgu__figure--hero">
            {assets.prosorguOneClick ? (
              <img
                src={assets.prosorguOneClick}
                alt="Uydu haritada seçili parsel ve sağda TKGM arazi tipi onayı, ProSorgu ve İlan Ver düğmeleri"
                loading="lazy"
              />
            ) : null}
            <figcaption>Harita + sınıflandırma paneli — tek tıkla devam</figcaption>
          </figure>
        </div>

        <div class="pp-landing-prosorgu__splitblock">
          <div class="pp-landing-prosorgu__splitblock-copy">
            <h3 class="pp-landing-prosorgu__subhead" id="arazim-bolunur-mu">
              Arazim bölünür mü?
            </h3>
            <p class="pp-landing-prosorgu__lede pp-landing-prosorgu__lede--tight">
              ProSorgu sonrası açılan analizde <strong>Bölünebilirlik</strong> sekmesi, parselin imar
              kurallarına göre kaç parçaya ayrılabileceğine dair teknik bir özet sunar.
            </p>
            <p class="pp-landing-prosorgu__disclaimer">
              Sonuçlar genel kurallar ve mahalle planı öncelikli modellerle üretilir; kesin hukuki işlem
              için belediye ve resmi kurumlara başvurmanız gerekir.
            </p>

            <h4 class="pp-landing-prosorgu__h4">1 · Özet sonuç kartları</h4>
            <p class="pp-landing-prosorgu__p">
              Üst bölümde <strong>Durum</strong> (ör. bölünebilir), <strong>Rejim</strong> (normal imar
              vb.), <strong>Kural kaynağı</strong> (mahalle planı gibi) ve{' '}
              <strong>tahmini yeni parsel sayısı</strong> tek bakışta yer alır.
            </p>

            <h4 class="pp-landing-prosorgu__h4">2 · Bilgi şeridi</h4>
            <p class="pp-landing-prosorgu__p">
              Mavi özet şerit, örneğin “bölünebilir görünüyor, yaklaşık üst sınır X yeni parsel” gibi
              yorumlanabilir bir cümleyle sonucu pekiştirir.
            </p>

            <h4 class="pp-landing-prosorgu__h4">3 · Kullanılan ölçüler ve uygulanan kurallar</h4>
            <p class="pp-landing-prosorgu__p">
              Sol tarafta parselden gelen <strong>alan</strong>, sınırlayıcı kutu uzun/kısa kenar,{' '}
              <strong>cephe sayısı</strong> ve <strong>yol bağlantısı</strong>; sağda ise minimum cephe
              ve derinlik, minimum parsel alanı, kamu yoluna cephe zorunluluğu ve ikinci ifraz kuralları
              yan yana okunur.
            </p>

            <h4 class="pp-landing-prosorgu__h4">4 · Gerekçeler (uygunluk kartları)</h4>
            <p class="pp-landing-prosorgu__p">
              Her kriter için ayrı kart: kamu yoluna cephe, asgari cephe ve derinlik, asgari parsel alanı
              ve tahmini parça sayısı — mevcut değerler ile gerekli eşikler karşılaştırılarak{' '}
              <strong>uygun</strong> sonucu açıklanır.
            </p>
          </div>
          <figure class="pp-landing-prosorgu__figure pp-landing-prosorgu__figure--split">
            {assets.prosorguBolunebilirlik ? (
              <img
                src={assets.prosorguBolunebilirlik}
                alt="Bölünebilirlik raporu: özet kartlar, ölçü ve kural tabloları, uygunluk gerekçeleri"
                loading="lazy"
              />
            ) : null}
            <figcaption>Bölünebilirlik ekranı — özet, tablolar ve gerekçeler</figcaption>
          </figure>
        </div>

        <h3 class="pp-landing-prosorgu__subhead" id="ilan-detay-ozellikler">
          ProSorgu ve ilan detay sayfası özellikleri
        </h3>
        <p class="pp-landing-prosorgu__lede">
          ProSorgu veya ilan detayında fiyat, risk ve skorları bir arada görürsünüz. Aşağıda önce{' '}
          <strong>Meyve Bahçesi Skoru</strong>, ardından puanlama ve özet ekranları özetlenmiştir.
        </p>

        <div class="pp-landing-prosorgu__orchard">
          <div class="pp-landing-prosorgu__orchard-copy">
            <h4 class="pp-landing-prosorgu__h4 pp-landing-prosorgu__h4--orchard">Meyve Bahçesi Skoru</h4>
            <p class="pp-landing-prosorgu__p">
              Parselin <strong>konumu ve iklim zonuna</strong> göre hangi meyve türlerinin daha uygun
              olduğunu <strong>0–100 uygunluk skoru</strong> ile sıralar; tarımsal yatırım ve kullanım
              senaryolarına hızlı bir ön fikir verir.
            </p>
            <ul class="pp-landing-prosorgu__orchard-list">
              <li>
                <strong>Öne çıkan tür</strong> — en yüksek skorlu meyve (ör. zeytin) vurgulu kutu
              </li>
              <li>
                <strong>İklim zonu</strong>, <strong>il özeti</strong>, <strong>ada / parsel</strong> ve{' '}
                <strong>sürüm</strong> bilgisi üst özet alanında
              </li>
              <li>
                <strong>Sıralı tablo</strong> — meyve türleri ve skorlar; karşılaştırmalı okuma
              </li>
            </ul>
          </div>
          <figure class="pp-landing-prosorgu__figure pp-landing-prosorgu__figure--orchard">
            {assets.prosorguMeyveBahcesi ? (
              <img
                src={assets.prosorguMeyveBahcesi}
                alt="Meyve Bahçesi Skoru: öne çıkan tür, iklim zonu, il ve ada parsel bilgisi, meyve türü sıralaması ve skorlar"
                loading="lazy"
              />
            ) : null}
            <figcaption>İlan / ProSorgu detay — Meyve Bahçesi Skoru paneli</figcaption>
          </figure>
        </div>

        <div class="pp-landing-prosorgu__orchard">
          <div class="pp-landing-prosorgu__orchard-copy">
            <h4 class="pp-landing-prosorgu__h4 pp-landing-prosorgu__h4--orchard">Puan sistemi</h4>
            <p class="pp-landing-prosorgu__p">
              Mahalle, fiyat, yol, imar ve kullanıcı gibi kriterlerin <strong>yüzde ağırlıkları</strong> ve
              yıldızlı genel not tek ekranda; ağırlık ve kaynak skorla{' '}
              <strong>şeffaf tablo</strong> halinde sunulur.
            </p>
            <ul class="pp-landing-prosorgu__orchard-list">
              <li>
                <strong>Kriterler</strong> — mahalle potansiyeli, fiyat konumu, yol erişimi, imarlı alana
                yakınlık
              </li>
              <li>
                <strong>Skorlar</strong> — mülk sezgisel skor, kullanıcı puanı
              </li>
              <li>
                <strong>Tablo</strong> — her satırda kaynak ve ağırlık; karşılaştırmalı okuma
              </li>
            </ul>
          </div>
          <figure class="pp-landing-prosorgu__figure pp-landing-prosorgu__figure--orchard">
            {assets.prosorguPuanSistemi ? (
              <img
                src={assets.prosorguPuanSistemi}
                alt="Puan sistemi: yıldızlı genel not ve mahalle, fiyat, yol, imar, kullanıcı gibi kriterlerin yüzde ağırlıkları"
                loading="lazy"
              />
            ) : null}
            <figcaption>İlan / ProSorgu detay — puan dağılımı ve ağırlıklar</figcaption>
          </figure>
        </div>

        <div class="pp-landing-prosorgu__orchard">
          <div class="pp-landing-prosorgu__orchard-copy">
            <h4 class="pp-landing-prosorgu__h4 pp-landing-prosorgu__h4--orchard">Arazi skoru ve metrikler</h4>
            <p class="pp-landing-prosorgu__p">
              Birleşik skor, ilan ve Pro birim fiyat, mahalle özeti ve alan; <strong>DFA</strong>, eğim ve
              parsel şekli gibi fiziksel metrikler ile <strong>imar ve yol</strong> sinyalleri tek panelde
              özetlenir.
            </p>
            <ul class="pp-landing-prosorgu__orchard-list">
              <li>
                <strong>Özet</strong> — birleşik skor, birim fiyatlar, mahalle özeti, alan
              </li>
              <li>
                <strong>Fiziksel</strong> — DFA çarpanı, grid hücresi, eğim, parsel şekli
              </li>
              <li>
                <strong>İmar / yol</strong> — imarsız kategori, yol sinyali, imarlı alan mesafesi
              </li>
            </ul>
          </div>
          <figure class="pp-landing-prosorgu__figure pp-landing-prosorgu__figure--orchard">
            {assets.prosorguAraziSkoru ? (
              <img
                src={assets.prosorguAraziSkoru}
                alt="Arazi skoru göstergesi, birim fiyatlar, DFA, eğim ve detaylı alt kriter çubukları"
                loading="lazy"
              />
            ) : null}
            <figcaption>İlan / ProSorgu detay — arazi skoru ve alt metrikler</figcaption>
          </figure>
        </div>

        <div class="pp-landing-prosorgu__orchard">
          <div class="pp-landing-prosorgu__orchard-copy">
            <h4 class="pp-landing-prosorgu__h4 pp-landing-prosorgu__h4--orchard">
              Olumlu sinyaller · Dikkat / risk
            </h4>
            <p class="pp-landing-prosorgu__p">
              Fiyat avantajı, eğim, geometri, yol erişimi ve imar mesafesi gibi{' '}
              <strong>olumlu işaretler</strong>; altyapı ve ilan eksikleri için{' '}
              <strong>risk uyarıları</strong> ayrı kutularda — fırsat ve risk yan yana okunur.
            </p>
            <ul class="pp-landing-prosorgu__orchard-list">
              <li>
                <strong>Olumlu</strong> — fiyat avantajı, eğim, geometri, yol erişimi, imar mesafesi
              </li>
              <li>
                <strong>Risk / dikkat</strong> — altyapı ve ilan eksikleri; uyarılar ayrı kutularda
              </li>
            </ul>
          </div>
          <figure class="pp-landing-prosorgu__figure pp-landing-prosorgu__figure--orchard">
            {assets.prosorguSinyaller ? (
              <img
                src={assets.prosorguSinyaller}
                alt="Olumlu sinyaller yeşil kutu, dikkat ve risk turuncu kutu"
                loading="lazy"
              />
            ) : null}
            <figcaption>İlan / ProSorgu detay — olumlu sinyaller ve risk kutuları</figcaption>
          </figure>
        </div>

        <figure class="pp-landing-prosorgu__figure pp-landing-prosorgu__figure--wide">
          {assets.prosorguIlanDetay ? (
            <img
              src={assets.prosorguIlanDetay}
              alt="İlan detay: özet puanlar, fiyat karşılaştırması, fiziksel ve risk bilgisi, sekme menüsü, harita ve görseller"
              loading="lazy"
            />
          ) : null}
          <figcaption>
            Tam sayfa ilan analizi — özet kartlar, KM / eğim / kenar / bölünebilirlik sekmeleri, harita ve
            görseller, iletişim ve değerleme özeti
          </figcaption>
        </figure>

        <ul class="pp-landing-prosorgu__bullets-footer">
          <li>
            <strong>Özet</strong> — mahalle ve mülk potansiyeli, fiyat avantajı yüzdesi, yıldız / durum
          </li>
          <li>
            <strong>Fiyat</strong> — piyasa, Pro birim ve ilan birim karşılaştırması
          </li>
          <li>
            <strong>Fiziksel &amp; risk</strong> — cephe, eğim, şekil; sulak, gerilim hattı, imar sınırı
          </li>
          <li>
            <strong>Sekmeler</strong> — açıklama, bilgi, uzman görüşleri, mesafe ve teknik analizler
          </li>
          <li>
            <strong>Meyve Bahçesi Skoru</strong> — iklim zonuna göre meyve türü uygunluğu (0–100)
          </li>
          <li>
            <strong>Medya</strong> — harita, fotoğraf, sokak görüntüsü; çoklu para birimi ve değerleme
            özeti
          </li>
        </ul>
      </div>
    </section>
  );
}

function ModulesPipeline() {
  return (
    <div class="pp-landing-modules-pipeline" aria-label="Sırada tanıtılacak modüller">
      <span class="pp-landing-modules-pipeline__label">Sırada</span>
      <ul class="pp-landing-modules-pipeline__list">
        <li class="pp-landing-modules-pipeline__item pp-landing-modules-pipeline__item--soon">
          <IconChartMap /> Arazi analizi
        </li>
        <li class="pp-landing-modules-pipeline__item pp-landing-modules-pipeline__item--soon">
          <IconExpert /> Uzman görüşü
        </li>
        <li class="pp-landing-modules-pipeline__item pp-landing-modules-pipeline__item--soon">
          <IconHome /> İlan ver
        </li>
      </ul>
    </div>
  );
}

const HERO_CAPABILITY_CARDS = [
  { label: 'ProSorgu', desc: 'Parsel ve bölge sorgularını profesyonelce yapın.', icon: 'prosorgu', href: '/harita/?landing_intent=prosorgu' },
  { label: 'Basit Sorgu', desc: 'Hızlı ve kolay parsel sorgulama aracı.', icon: 'search', href: '/harita/?landing_intent=basit' },
  { label: 'İlan Ver', desc: 'Analiz ettiğiniz araziyi vitrinde yayınlayın.', icon: 'plus', href: '/portal/ilan/olustur/' },
  { label: 'Emlak Vitrini', desc: 'Portföyünüzü geniş kitlelere sergileyin.', icon: 'listing', href: '/portal/ilan/vitrin/' },
  {
    label: 'Sandık',
    desc: 'Gayrimenkul danışmanlarına özel gizli ilan portalı.',
    icon: 'vault',
    href: PORTAL_SANDIK_URL,
    authGate: true,
  },
  { label: 'ProSorgu Listesi', desc: 'Yapılan diğer sorgu sonuçları görün.', icon: 'prosorgu', href: '/portal/recent-queries/?ref=recent30' },
  { label: 'Tasarımlı Ekran Görüntüsü', desc: 'Parsel seçtikten sonra sunuma hazır görüntü alın.', icon: 'camera', href: '/harita/?landing_intent=designed_screenshot' },
  { label: '3D Model', desc: 'Araziyi 3D ortamda inceleyin.', icon: 'cube', href: '/harita/?landing_intent=3d_model' },
  { label: 'AI Drone', desc: 'Yapay zeka destekli drone görüntü analizi.', icon: 'drone', href: '/harita/?landing_intent=ai_drone' },
  { label: 'Uzman Görüşü', desc: 'ProSorgu sonrası uzman talebi oluşturun.', icon: 'expert', href: '/harita/?landing_intent=expert_opinion' },
  { label: 'Hisseli Parsel', desc: 'Hisseli parselleri analiz edin ve yönetin.', icon: 'split', href: '/harita/?landing_intent=hisseli_parsel' },
  { label: 'AI Video', desc: 'Yapay zeka ile arazi videoları oluşturun.', icon: 'video', href: '/accounts/3d-editor-purchase/?target=ai_video&next=/portal/drone-editor/%3Fmode%3Dai_video' },
];

function resolveCapabilityCards(isAuthenticated) {
  return HERO_CAPABILITY_CARDS.map((item) => {
    if (!item.authGate) return item;
    const target = item.href || PORTAL_SANDIK_URL;
    return {
      ...item,
      href: isAuthenticated
        ? target
        : `/accounts/login/?next=${encodeURIComponent(target)}`,
    };
  });
}

function CapabilityCardLink({ item, className = 'pp-landing-hero-capabilities__card' }) {
  return (
    <a class={className} role="listitem" href={item.href}>
      <div class="pp-landing-hero-capabilities__top">
        <span
          class={`pp-landing-hero-capabilities__icon pp-landing-hero-capabilities__icon--${item.icon}`}
          aria-hidden="true"
        />
        <strong>{item.label}</strong>
      </div>
      <div class="pp-landing-hero-capabilities__body">
        <p>{item.desc}</p>
      </div>
      <span class="pp-landing-hero-capabilities__arrow" aria-hidden="true">›</span>
    </a>
  );
}

function HeroQuickActions() {
  const isAuthenticated = usePpPortalAuthenticated();
  const cards = useMemo(() => resolveCapabilityCards(isAuthenticated), [isAuthenticated]);
  const basit = cards.find((c) => c.label === 'Basit Sorgu');
  const prosorgu = cards.find((c) => c.label === 'ProSorgu');
  if (!basit || !prosorgu) return null;

  return (
    <div class="pp-landing-hero__visual-top" role="list" aria-label="Hızlı sorgu">
      <HeroQueryCard item={basit} variant="basic" />
      <HeroQueryCard item={prosorgu} variant="pro" />
    </div>
  );
}

function HeroPartnerStrip({ registerUrlCorporate }) {
  return (
    <div
      class="pp-landing-hero__partner-strip pp-landing-hero__partner-strip--in-visual"
      role="region"
      aria-labelledby="hero-partner-heading"
    >
      <div class="pp-landing-hero__partner-copy">
        <span class="pp-landing-hero__partner-mark" aria-hidden="true" />
        <div class="pp-landing-hero__partner-text">
          <p id="hero-partner-heading" class="pp-landing-hero__partner-title">
            Güçlü iş ortaklarımızla daha güçlüyüz.
          </p>
          <p class="pp-landing-hero__partner-desc">
            Gayrimenkul firması, SPK lisanslı değerleme ofisi veya LİHKAB bürosuysanız
            sistemimize katılın, kazanın.
          </p>
        </div>
      </div>
      <div class="pp-landing-hero__partner-actions">
        <a
          class="pp-landing-hero__partner-chip pp-landing-hero__partner-chip--emlak"
          href={registerUrlCorporate('real_estate')}
        >
          <span class="pp-landing-hero__partner-chip-icon" aria-hidden="true" />
          <span class="pp-landing-hero__partner-chip-label">Emlak Firması</span>
        </a>
        <a
          class="pp-landing-hero__partner-chip pp-landing-hero__partner-chip--lihkab"
          href={registerUrlCorporate('lihkab')}
        >
          <span class="pp-landing-hero__partner-chip-icon" aria-hidden="true" />
          <span class="pp-landing-hero__partner-chip-label">LİHKAB</span>
        </a>
        <a
          class="pp-landing-hero__partner-chip pp-landing-hero__partner-chip--spk"
          href={registerUrlCorporate('spk')}
        >
          <span class="pp-landing-hero__partner-chip-icon" aria-hidden="true" />
          <span class="pp-landing-hero__partner-chip-label">SPK Lisanslı</span>
        </a>
      </div>
    </div>
  );
}

function LandingCapabilitiesSection() {
  const isAuthenticated = usePpPortalAuthenticated();
  const cards = useMemo(() => resolveCapabilityCards(isAuthenticated), [isAuthenticated]);

  return (
    <section
      class="pp-landing-capabilities"
      id="landing-capabilities"
      aria-labelledby="landing-capabilities-title"
    >
      <div class="pp-landing-inner pp-landing-capabilities__inner">
        <header class="pp-landing-capabilities__head">
          <h2 id="landing-capabilities-title" class="pp-landing-capabilities__title">
            Yapabilecekleriniz
          </h2>
          <p class="pp-landing-capabilities__lead">
            Parsel sorgusu, vitrin, 3D ve yapay zeka araçları — tek platformda.
          </p>
        </header>

        <div class="pp-landing-capabilities__panel">
          <div class="pp-landing-hero-capabilities__grid" role="list">
            {cards.map((item) => (
              <CapabilityCardLink item={item} key={item.label} />
            ))}
          </div>
        </div>

        <div class="pp-landing-capabilities__footer">
          <div class="pp-landing-capabilities__footer-nav" role="group" aria-label="Sayfa gezinme">
            <button type="button" class="pp-landing-scroll-hint" data-landing-scroll="up">
              <span class="pp-landing-scroll-hint__label">Ana sayfa</span>
              <i class="fas fa-chevron-up" aria-hidden="true" />
            </button>
            <button
              type="button"
              class="pp-landing-scroll-hint"
              data-landing-scroll="down"
              aria-label="Tepe Kredi kazanım fırsatlarını gör"
            >
              <span class="pp-landing-scroll-hint__label">Kazanım fırsatları</span>
              <i class="fas fa-chevron-down" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/** Landing — “Nasıl Çalışır?” üstü: profesyonel üyelik kartları (SVG görseller) */
function PartnerProVisualRealEstate() {
  return (
    <svg
      class="pp-landing-b2b-card__svg"
      viewBox="0 0 320 200"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="pp-b2b-re-grad" x1="40" y1="20" x2="280" y2="180" gradientUnits="userSpaceOnUse">
          <stop stopColor="#bfdbfe" />
          <stop offset="1" stopColor="#e0f2fe" />
        </linearGradient>
        <linearGradient id="pp-b2b-re-build" x1="0" y1="0" x2="0" y2="1">
          <stop stopColor="#3b82f6" />
          <stop offset="1" stopColor="#1d4ed8" />
        </linearGradient>
      </defs>
      <rect x="24" y="32" width="272" height="136" rx="16" fill="url(#pp-b2b-re-grad)" opacity="0.95" />
      <path
        d="M72 148V88l48-28 48 28v60h-32v-36h-32v36H72z"
        fill="url(#pp-b2b-re-build)"
        opacity="0.92"
      />
      <rect x="108" y="112" width="20" height="36" rx="2" fill="#e0f2fe" />
      <circle cx="200" cy="56" r="22" fill="#fef3c7" stroke="#f59e0b" strokeWidth="2" />
      <path
        d="M200 42l4 10h10l-8 6 3 10-9-6-9 6 3-10-8-6h10l4-10z"
        fill="#f59e0b"
      />
      <circle cx="248" cy="120" r="28" fill="#fff" stroke="#2f5f9a" strokeWidth="3" />
      <path d="M248 108v24M236 120h24" stroke="#2f5f9a" strokeWidth="3" strokeLinecap="round" />
      <rect x="52" y="156" width="96" height="28" rx="8" fill="#fff" stroke="#2f5f9a" strokeWidth="2" />
      <text x="100" y="175" textAnchor="middle" fill="#163a66" fontSize="11" fontWeight="700" fontFamily="system-ui,sans-serif">
        Ücretsiz kullanım
      </text>
    </svg>
  );
}

function PartnerProVisualSpk() {
  return (
    <svg
      class="pp-landing-b2b-card__svg"
      viewBox="0 0 320 200"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="pp-b2b-spk-bg" x1="0" y1="0" x2="320" y2="200" gradientUnits="userSpaceOnUse">
          <stop stopColor="#ecfdf5" />
          <stop offset="1" stopColor="#d1fae5" />
        </linearGradient>
      </defs>
      <rect x="28" y="24" width="264" height="152" rx="18" fill="url(#pp-b2b-spk-bg)" />
      <rect x="56" y="44" width="208" height="120" rx="10" fill="#fff" stroke="#059669" strokeWidth="2" />
      <rect x="72" y="60" width="120" height="8" rx="4" fill="#a7f3d0" />
      <rect x="72" y="78" width="176" height="6" rx="3" fill="#d1fae5" />
      <rect x="72" y="92" width="160" height="6" rx="3" fill="#d1fae5" />
      <rect x="72" y="106" width="140" height="6" rx="3" fill="#d1fae5" />
      <circle cx="232" cy="96" r="28" fill="#059669" opacity="0.15" />
      <path
        d="M232 82v28M218 96h28"
        stroke="#059669"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <text x="232" y="102" textAnchor="middle" fill="#047857" fontSize="20" fontWeight="800" fontFamily="system-ui,sans-serif">
        ₺
      </text>
      <rect x="88" y="128" width="144" height="22" rx="6" fill="#059669" />
      <text x="160" y="143" textAnchor="middle" fill="#fff" fontSize="10" fontWeight="700" fontFamily="system-ui,sans-serif">
        SPK değerleme talebi
      </text>
    </svg>
  );
}

function PartnerProVisualLihkab() {
  return (
    <svg
      class="pp-landing-b2b-card__svg"
      viewBox="0 0 320 200"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="pp-b2b-li-map" x1="0" y1="0" x2="1" y2="1">
          <stop stopColor="#e0f2fe" />
          <stop offset="1" stopColor="#f8fafc" />
        </linearGradient>
      </defs>
      <rect x="20" y="36" width="280" height="128" rx="14" fill="url(#pp-b2b-li-map)" stroke="#94a3b8" strokeWidth="1.5" />
      <path d="M36 140 L80 72 L120 108 L168 52 L220 96 L284 60" stroke="#2f5f9a" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="168" cy="52" r="6" fill="#ef4444" stroke="#fff" strokeWidth="2" />
      <circle cx="220" cy="96" r="5" fill="#2f5f9a" />
      <rect x="200" y="44" width="88" height="56" rx="8" fill="#fff" stroke="#64748b" strokeWidth="2" />
      <path d="M244 88 L244 56 M228 72h32" stroke="#475569" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="244" cy="96" r="14" stroke="#0f172a" strokeWidth="2.5" fill="none" />
      <path d="M244 90v12M238 96h12" stroke="#0f172a" strokeWidth="2" strokeLinecap="round" />
      <text x="244" y="154" textAnchor="middle" fill="#334155" fontSize="10" fontWeight="600" fontFamily="system-ui,sans-serif">
        Harita · aplikasyon
      </text>
    </svg>
  );
}

function PartnerProfessionalSection() {
  return (
    <section class="pp-landing-section pp-landing-section--partner-pro" aria-labelledby="partner-pro-heading">
      <div class="pp-landing-inner">
        <div class="pp-landing-section__head pp-landing-section__head--center pp-landing-section__head--partner">
          <p class="pp-landing-section__eyebrow">Profesyoneller</p>
          <h2 id="partner-pro-heading">Talep alın, kazanç veya ücretsiz kullanım kazanın</h2>
          <p class="pp-landing-section__lead">
            Emlak profesyonelleri, SPK lisanslı değerleme kuruluşları ve LİHKAB büroları için platform üzerinden yürütülen
            iş modelleri.
          </p>
        </div>
        <div class="pp-landing-b2b-grid" role="list">
          <article class="pp-landing-b2b-card" role="listitem">
            <div class="pp-landing-b2b-card__visual pp-landing-b2b-card__visual--re">
              <PartnerProVisualRealEstate />
            </div>
            <div class="pp-landing-b2b-card__body">
              <h3>Emlak firmaları ve danışmanlar</h3>
              <p>
                Uzmanı olduğunuz bölgelere düşen talepleri anında görün; doğru yönlendirme ve yanıtlarınızla{' '}
                <strong>ücretsiz kullanım hakkı</strong> kazanın. Müşteri akışını tek ekrandan yönetin, görünürlüğünüzü ve
                güvenilirliğinizi artırın.
              </p>
            </div>
          </article>
          <article class="pp-landing-b2b-card" role="listitem">
            <div class="pp-landing-b2b-card__visual pp-landing-b2b-card__visual--spk">
              <PartnerProVisualSpk />
            </div>
            <div class="pp-landing-b2b-card__body">
              <h3>SPK lisanslı değerleme firmaları</h3>
              <p>
                Kullanıcıların ilettiği <strong>SPK değerleme taleplerini</strong> üstlenin; süreçlerinizi dijitalleştirin ve{' '}
                <strong>doğrudan gelir elde edin</strong>. Teklif, iş paylaşımı ve rapor akışı aynı platformda bir araya gelir.
              </p>
            </div>
          </article>
          <article class="pp-landing-b2b-card" role="listitem">
            <div class="pp-landing-b2b-card__visual pp-landing-b2b-card__visual--li">
              <PartnerProVisualLihkab />
            </div>
            <div class="pp-landing-b2b-card__body">
              <h3>LİHKAB büroları</h3>
              <p>
                Parsel ve harita üzerindeki <strong>ölçüm, aplikasyon ve harita işlemleri taleplerini</strong> karşılayın; gelen
                işleri kabul ederek <strong>kazanç sağlayın</strong>. Teknik hizmet talepleri size yönlendirilir, takviminizi
                siz yönetirsiniz.
              </p>
            </div>
          </article>
        </div>
      </div>
    </section>
  );
}

function ListingVisualMock() {
  return (
    <div class="pp-landing-market" aria-hidden="true">
      <div class="pp-landing-market__ground" />
      <div class="pp-landing-market__pin pp-landing-market__pin--1">
        <i class="fas fa-map-marker-alt" aria-hidden="true"></i>
      </div>
      <div class="pp-landing-market__pin pp-landing-market__pin--2">
        <i class="fas fa-map-marker-alt" aria-hidden="true"></i>
      </div>
      <div class="pp-landing-market__card pp-landing-market__card--1">
        <span>Arsa · Bursa</span>
        <strong>₺ · m²</strong>
      </div>
      <div class="pp-landing-market__card pp-landing-market__card--2">
        <span>Tarla · İzmir</span>
        <strong>Analiz hazır</strong>
      </div>
      <div class="pp-landing-market__trees" />
    </div>
  );
}

function earnRewardHref(appBase, eventType) {
  const base = `${appBase}/accounts/pricing/`;
  if (eventType && /^[a-z0-9_]+$/i.test(eventType)) {
    return `${base}#${eventType}`;
  }
  return `${base}#tepecoin-earn`;
}

function LandingEarnCard({ item, appBase }) {
  const faClass = item.icon_fa || 'fa-gift';
  const href = earnRewardHref(appBase, item.event_type);
  const body = (
    <>
      <div class="pp-landing-earn__card-top">
        <span class="pp-landing-earn__icon" aria-hidden="true">
          <i class={`fas ${faClass}`} />
        </span>
        <strong>{item.display_name}</strong>
        {item.is_coming_soon ? <span class="pp-landing-earn__soon">Yakında</span> : null}
      </div>
      <div class="pp-landing-earn__card-body">
        <p>{item.description || 'Detaylar için paket sayfasına gidin.'}</p>
      </div>
      <span class="pp-landing-earn__badge">{item.credits} Tepe Kredi</span>
      {!item.is_coming_soon ? <span class="pp-landing-earn__arrow" aria-hidden="true">›</span> : null}
    </>
  );

  if (item.is_coming_soon) {
    return (
      <div class="pp-landing-earn__card pp-landing-earn__card--soon" role="listitem">
        {body}
      </div>
    );
  }

  return (
    <a class="pp-landing-earn__card" role="listitem" href={href}>
      {body}
    </a>
  );
}

const USAGE_ACTION_PATHS = {
  pro_query: '/harita/?landing_intent=prosorgu',
  ilan: '/portal/ilan/olustur/',
  '3d_design': '/harita/?landing_intent=3d_model',
  parcel_split_view: '/harita/?landing_intent=hisseli_parsel',
  drone_video: '/accounts/3d-editor-purchase/?target=drone_video&next=/portal/drone-editor/%3Fmode%3Ddrone',
  ai_video: '/accounts/3d-editor-purchase/?target=ai_video&next=/portal/drone-editor/%3Fmode%3Dai_video',
  ai_img: '/harita/?landing_intent=ai_drone',
  ai_drone: '/harita/?landing_intent=ai_drone',
  ai_drone_frame: '/harita/?landing_intent=ai_drone',
  ia_drone_realimg: '/harita/?landing_intent=ai_drone',
  drone_ref_image_extra: '/portal/drone-editor/',
  drone_preflight_retry: '/portal/drone-editor/',
  drone_runway_retry: '/portal/drone-editor/',
  expert_consultancy: '/harita/?landing_intent=expert_opinion',
  proparcel_gorusu: '/harita/?landing_intent=expert_opinion',
  model_purchase: '/accounts/pricing/',
  listing_pro_sorgu_unlock: '/portal/ilan/vitrin/',
};

function usageActionHref(appBase, actionType) {
  const key = String(actionType || '').trim();
  const path = USAGE_ACTION_PATHS[key];
  if (path) {
    return `${appBase}${path}`;
  }
  return `${appBase}/accounts/pricing/`;
}

function LandingUsageCard({ item, appBase }) {
  const faClass = item.icon_fa || 'fa-circle';
  const href = usageActionHref(appBase, item.action_type);
  return (
    <a
      class="pp-landing-usage__card"
      role="listitem"
      href={href}
      aria-label={`${item.display_name} — ${item.credits} Tepe Kredi`}
    >
      <span class="pp-landing-usage__icon" aria-hidden="true">
        <i class={`fas ${faClass}`} />
      </span>
      <span class="pp-landing-usage__credits" aria-label={`${item.credits} Tepe Kredi`}>
        {item.credits}
      </span>
      <strong class="pp-landing-usage__name">{item.display_name}</strong>
      {item.description ? <p class="pp-landing-usage__desc">{item.description}</p> : null}
      <span class="pp-landing-usage__unit">Tepe Kredi</span>
      <span class="pp-landing-usage__arrow" aria-hidden="true">›</span>
    </a>
  );
}

function LandingCreditUsageAreas({ appBase, bankBadges = [], siteSslType = 'dv', companyAddress = '' }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/credit/costs/?visible_on_pricing=1', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('credit-costs'))))
      .then((data) => {
        if (!cancelled) setItems(Array.isArray(data?.items) ? data.items : []);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section class="pp-landing-usage" id="tepecoin-usage" aria-labelledby="landing-usage-title">
      <div class="pp-landing-inner pp-landing-usage__inner">
        <header class="pp-landing-usage__head">
          <span class="pp-landing-usage__eyebrow">
            <i class="fas fa-coins" aria-hidden="true" />
            Tepe Kredi
          </span>
          <h2 id="landing-usage-title" class="pp-landing-usage__title">
            Kredi Kullanım Alanları
          </h2>
          <p class="pp-landing-usage__lead">
            Her özellik için harcanan Tepe Kredi miktarı — güncel maliyetler veritabanından yüklenir.
          </p>
        </header>

        {loading ? (
          <p class="pp-landing-usage__status" role="status">
            <i class="fas fa-spinner fa-spin" aria-hidden="true" /> Yükleniyor…
          </p>
        ) : null}

        {!loading && error ? (
          <p class="pp-landing-usage__status pp-landing-usage__status--error">
            Kullanım alanları yüklenemedi.{' '}
            <a href={`${appBase}/accounts/pricing/`}>Paket sayfasına git</a>
          </p>
        ) : null}

        {!loading && !error && items.length === 0 ? (
          <p class="pp-landing-usage__status">Henüz kullanım maliyeti tanımlanmamış.</p>
        ) : null}

        {!loading && !error && items.length > 0 ? (
          <div class="pp-landing-usage__grid" role="list">
            {items.map((item) => (
              <LandingUsageCard
                key={item.action_type || item.display_name}
                item={item}
                appBase={appBase}
              />
            ))}
          </div>
        ) : null}

        <div class="pp-landing-usage__footer">
          <a class="pp-landing-chip pp-landing-chip--dark" href={`${appBase}/accounts/pricing/`}>
            Paket ve kredi satın al
          </a>
          <div class="pp-landing-usage__footer-nav" role="group" aria-label="Sayfa gezinme">
            <button type="button" class="pp-landing-scroll-hint" data-landing-scroll="up">
              <span class="pp-landing-scroll-hint__label">Kazanım fırsatları</span>
              <i class="fas fa-chevron-up" aria-hidden="true" />
            </button>
          </div>
        </div>
        <LandingFooterBand
          bankBadges={bankBadges}
          siteSslType={siteSslType}
          companyAddress={companyAddress}
        />
      </div>
    </section>
  );
}

function LandingEarnOpportunities({ appBase }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/credit/gift-rewards/', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('gift-rewards'))))
      .then((data) => {
        if (!cancelled) setItems(Array.isArray(data?.items) ? data.items : []);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section class="pp-landing-earn" id="tepecoin-earn" aria-labelledby="landing-earn-title">
      <div class="pp-landing-inner pp-landing-earn__inner">
        <header class="pp-landing-earn__head">
          <span class="pp-landing-earn__eyebrow">
            <i class="fas fa-gift" aria-hidden="true" />
            Tepe Kredi
          </span>
          <h2 id="landing-earn-title" class="pp-landing-earn__title">
            Tepe Kredi Kazanım Şansları
          </h2>
          <p class="pp-landing-earn__lead">Öner, paylaş, değerlendir — Tepe Kredi kazan.</p>
        </header>

        {loading ? (
          <p class="pp-landing-earn__status" role="status">
            <i class="fas fa-spinner fa-spin" aria-hidden="true" /> Yükleniyor…
          </p>
        ) : null}

        {!loading && error ? (
          <p class="pp-landing-earn__status pp-landing-earn__status--error">
            Kazanım fırsatları yüklenemedi.{' '}
            <a href={`${appBase}/accounts/pricing/#tepecoin-earn`}>Paket sayfasına git</a>
          </p>
        ) : null}

        {!loading && !error && items.length === 0 ? (
          <p class="pp-landing-earn__status">Henüz kazanım fırsatı tanımlanmamış.</p>
        ) : null}

        {!loading && !error && items.length > 0 ? (
          <div class="pp-landing-earn__grid" role="list">
            {items.map((item) => (
              <LandingEarnCard
                key={item.event_type || item.display_name}
                item={item}
                appBase={appBase}
              />
            ))}
          </div>
        ) : null}

        <div class="pp-landing-earn__footer">
          <a class="pp-landing-chip pp-landing-chip--dark" href={`${appBase}/accounts/pricing/#tepecoin-earn`}>
            Tüm kazanım seçenekleri
          </a>
          <div class="pp-landing-earn__footer-nav" role="group" aria-label="Sayfa gezinme">
            <button type="button" class="pp-landing-scroll-hint" data-landing-scroll="up">
              <span class="pp-landing-scroll-hint__label">Yapabilecekleriniz</span>
              <i class="fas fa-chevron-up" aria-hidden="true" />
            </button>
            <button
              type="button"
              class="pp-landing-scroll-hint"
              data-landing-scroll="down"
              aria-label="Kredi kullanım alanlarını gör"
            >
              <span class="pp-landing-scroll-hint__label">Kullanım alanları</span>
              <i class="fas fa-chevron-down" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

const LANDING_FP_SECTIONS = [
  { anchor: 'hero', label: 'Ana sayfa' },
  { anchor: 'capabilities', label: 'Yapabilecekleriniz' },
  { anchor: 'earn', label: 'Tepe Kredi kazanım' },
  { anchor: 'usage', label: 'Kredi kullanım alanları' },
];

const HERO_FLY_STEP_COUNT = 9;
const HERO_FLY_INITIAL_DELAY_MS = 480;
const HERO_FLY_STAGGER_MS = 280;

function heroFlyItemClass(step, visibleStep) {
  return `pp-landing-hero__fly-item${visibleStep >= step ? ' is-visible' : ''}`;
}

function useHeroFlyUpSequence(reduceMotion) {
  const [visibleStep, setVisibleStep] = useState(() =>
    reduceMotion ? HERO_FLY_STEP_COUNT : 0,
  );

  useEffect(() => {
    if (reduceMotion) return undefined;
    const timers = [];
    for (let i = 0; i < HERO_FLY_STEP_COUNT; i += 1) {
      timers.push(
        window.setTimeout(
          () => setVisibleStep(i + 1),
          HERO_FLY_INITIAL_DELAY_MS + i * HERO_FLY_STAGGER_MS,
        ),
      );
    }
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [reduceMotion]);

  return visibleStep;
}

function HeroLeftCard({ assets, mapUrl, visibleStep, registrationCredits = 0 }) {
  const giftAmount = Number(registrationCredits) || 0;
  const showGift = giftAmount > 0;
  return (
    <div class="pp-landing-hero__card">
      <span class={`pp-landing-hero__eyebrow ${heroFlyItemClass(1, visibleStep)}`}>
        <img
          class="pp-landing-hero__eyebrow-icon"
          src={assets.favicon}
          alt=""
          width={31}
          height={31}
          decoding="async"
        />
        <span class="pp-landing-hero__eyebrow-text">ProParcel</span>
      </span>
      <h1 id="landing-hero-title" class="pp-landing-hero__title">
        <span class={`pp-landing-hero__title-line ${heroFlyItemClass(2, visibleStep)}`}>
          Ada Parsel Sorgula
        </span>
        <span class={`pp-landing-hero__title-line ${heroFlyItemClass(3, visibleStep)}`}>
          Ücretsiz Parsel Sorgulama
        </span>
      </h1>
      <p class={`pp-landing-hero__sub ${heroFlyItemClass(4, visibleStep)}`}>
        Ada parsel sorgula, haritada gör, analiz et — arazi yatırımının yeni platformu.
      </p>
      {showGift ? (
      <div
        class="pp-landing-hero__gift-card pp-landing-hero__gift-card--in-card"
        role="region"
        aria-label="Yeni üyelere başlangıç hediyesi"
      >
        <p class={`pp-landing-hero__gift-eyebrow ${heroFlyItemClass(5, visibleStep)}`}>
          Yeni üyelere başlangıç hediyesi
        </p>
        <p
          class={`pp-landing-hero__gift-credit ${heroFlyItemClass(6, visibleStep)}`}
          aria-label={`${giftAmount} Kredi`}
        >
          <span class="pp-landing-hero__gift-amount">{giftAmount}</span>
          <span class="pp-landing-hero__gift-unit">Kredi</span>
        </p>
        <div class={`pp-landing-hero__gift-rule ${heroFlyItemClass(6, visibleStep)}`} aria-hidden="true" />
        <span class={`pp-landing-hero__gift-badge ${heroFlyItemClass(7, visibleStep)}`}>BEDAVA</span>
        <p class={`pp-landing-hero__gift-foot ${heroFlyItemClass(8, visibleStep)}`}>
          Arazi analizlerini ücretsiz deneyin.
        </p>
      </div>
      ) : null}
      <a
        class={`pp-landing-hero__cta pp-landing-hero__cta--card-bottom ${heroFlyItemClass(9, visibleStep)}`}
        href={mapUrl}
      >
        Başla
      </a>
    </div>
  );
}

function App({ assets }) {
  const viewportRef = useRef(null);
  const animatingRef = useRef(false);
  const activeSectionRef = useRef(0);
  const [activeSection, setActiveSection] = useState(0);
  const [registrationCredits, setRegistrationCredits] = useState(0);
  const reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const appBase = typeof window !== 'undefined' ? window.location.origin : '';
  const mapUrl = `${appBase}/harita/`;
  const registerUrl = `${appBase}/accounts/register/`;
  const ilanCreateUrl = `${appBase}/portal/ilan/olustur/`;
  const registerUrlCorporate = (corporateType) =>
    `${appBase}/accounts/register/?member_type=corporate&corporate_type=${encodeURIComponent(corporateType)}`;
  const heroBg = assets.heroBg || '/media/landingpage/sluet3.png';
  const fixedBgStyle = { backgroundImage: `url("${heroBg}")` };
  const heroFlyStep = useHeroFlyUpSequence(reduceMotion);

  const goToSection = (index) => {
    const next = Math.max(0, Math.min(LANDING_FP_SECTIONS.length - 1, index));
    if (next === activeSectionRef.current) return;
    animatingRef.current = true;
    activeSectionRef.current = next;
    setActiveSection(next);
    window.setTimeout(() => {
      animatingRef.current = false;
    }, 780);
  };

  useEffect(() => {
    let cancelled = false;
    fetch(`${appBase}/api/gifts/`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data?.registration) return;
        setRegistrationCredits(Number(data.registration.default) || 0);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [appBase]);

  useEffect(() => {
    activeSectionRef.current = activeSection;
  }, [activeSection]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return undefined;

    const getSlideBody = (index) =>
      viewport.querySelector(`[data-slide="${index}"] .pp-landing-slide__body`);

    const onWheel = (e) => {
      if (animatingRef.current) {
        e.preventDefault();
        return;
      }

      const idx = activeSectionRef.current;
      const body = getSlideBody(idx);
      const goingDown = e.deltaY > 0;

      if (body && body.scrollHeight > body.clientHeight + 2) {
        if (goingDown && body.scrollTop + body.clientHeight < body.scrollHeight - 4) return;
        if (!goingDown && body.scrollTop > 4) return;
      }

      e.preventDefault();
      if (goingDown && idx < LANDING_FP_SECTIONS.length - 1) goToSection(idx + 1);
      else if (!goingDown && idx > 0) goToSection(idx - 1);
    };

    const onHintClick = (e) => {
      const btn = e.target.closest('[data-landing-scroll]');
      if (!btn || !viewport.contains(btn)) return;
      const dir = btn.getAttribute('data-landing-scroll');
      const idx = activeSectionRef.current;
      if (dir === 'down') goToSection(Math.min(LANDING_FP_SECTIONS.length - 1, idx + 1));
      else if (dir === 'up') goToSection(Math.max(0, idx - 1));
    };

    const onNavClick = (e) => {
      const btn = e.target.closest('[data-landing-fp-index]');
      if (!btn) return;
      const next = Number(btn.getAttribute('data-landing-fp-index'));
      if (!Number.isNaN(next)) goToSection(next);
    };

    viewport.addEventListener('wheel', onWheel, { passive: false });
    viewport.addEventListener('click', onHintClick);
    document.addEventListener('click', onNavClick);

    return () => {
      viewport.removeEventListener('wheel', onWheel);
      viewport.removeEventListener('click', onHintClick);
      document.removeEventListener('click', onNavClick);
    };
  }, []);

  return (
    <div class="pp-landing pp-landing--ready">
      <div class="pp-landing-fixed-bg is-visible" style={fixedBgStyle} aria-hidden="true" />
      <div class="pp-landing-fixed-overlay is-visible" aria-hidden="true" />

      <nav class="pp-landing-fp-nav" aria-label="Sayfa bölümleri">
        {LANDING_FP_SECTIONS.map((section, index) => (
          <button
            type="button"
            key={section.anchor}
            class={`pp-landing-fp-nav__dot${activeSection === index ? ' is-active' : ''}`}
            data-landing-fp-index={index}
            aria-label={section.label}
            aria-current={activeSection === index ? 'true' : undefined}
          />
        ))}
      </nav>

      <div class="pp-landing-viewport" ref={viewportRef}>
        <div
          class="pp-landing-slides"
          style={{ transform: `translate3d(0, ${-activeSection * 100}vh, 0)` }}
        >
          <div class="pp-landing-slide" data-slide="0" data-anchor="hero">
            <div class="pp-landing-slide__body">
          <section
            class="pp-landing-hero pp-landing-hero--scene"
            aria-labelledby="landing-hero-title"
          >
        <div class="pp-landing-hero__vignette" aria-hidden="true" />
        <div class="pp-landing-hero__bottom-gradient" aria-hidden="true" />

        <div class="pp-landing-inner pp-landing-hero__scene-inner">
          <div class="pp-landing-hero__row">
            <div class="pp-landing-hero__col pp-landing-hero__col--text">
              <HeroLeftCard assets={assets} mapUrl={mapUrl} visibleStep={heroFlyStep} registrationCredits={registrationCredits} />
            </div>
            <div class="pp-landing-hero__col pp-landing-hero__col--visual">
              <div class="pp-landing-hero__visual-stack">
                <HeroQuickActions />
                <div class="pp-landing-hero__visual-bottom">
                  <HeroPartnerStrip registerUrlCorporate={registerUrlCorporate} />
                </div>
              </div>
            </div>
          </div>

          <button
            type="button"
            class="pp-landing-scroll-hint pp-landing-scroll-hint--down"
            data-landing-scroll="down"
            aria-label="Yapabilecekleriniz bölümüne git"
          >
            <span class="pp-landing-scroll-hint__label">Yapabilecekleriniz</span>
            <i class="fas fa-chevron-down" aria-hidden="true" />
          </button>
        </div>
      </section>
            </div>
          </div>

          <div class="pp-landing-slide" data-slide="1" data-anchor="capabilities">
            <div class="pp-landing-slide__body">
              <LandingCapabilitiesSection />
            </div>
          </div>

          <div class="pp-landing-slide" data-slide="2" data-anchor="earn">
            <div class="pp-landing-slide__body">
              <LandingEarnOpportunities appBase={appBase} />
            </div>
          </div>

          <div class="pp-landing-slide" data-slide="3" data-anchor="usage">
            <div class="pp-landing-slide__body">
              <LandingCreditUsageAreas
                appBase={appBase}
                bankBadges={assets.bankBadges || []}
                siteSslType={assets.siteSslType || 'dv'}
                companyAddress={assets.companyAddress || ''}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const assets = readAssets();
const root = document.getElementById('landing-root');
if (root) {
  root.removeAttribute('aria-busy');
  render(<App assets={assets} />, root);
}
